// src/components/Footer.js

import React, { useState, useEffect } from 'react';
import logoImage from "../assets/images/Geritage.png";

// Variable global para evitar renderizados duplicados
let hasFooterRendered = false;

const Footer = () => {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (!hasFooterRendered) {
      hasFooterRendered = true;
      setShouldRender(true);
    }
  }, []);

  if (!shouldRender) return null;

  return (
    <>
      {/* Keyframes y estilos para enlaces y separador */}
      <style>{`
        @keyframes fadeInPulseOut {
          0% { 
            opacity: 0; 
            transform: perspective(800px) rotateY(20deg) rotateX(10deg) scale(0.95); 
          }
          15% { 
            opacity: 1; 
            transform: perspective(800px) rotateY(20deg) rotateX(10deg) scale(1); 
          }
          50% { 
            opacity: 1; 
            transform: perspective(800px) rotateY(20deg) rotateX(10deg) scale(1.05); 
          }
          85% { 
            opacity: 1; 
            transform: perspective(800px) rotateY(20deg) rotateX(10deg) scale(1); 
          }
          100% { 
            opacity: 0; 
            transform: perspective(800px) rotateY(20deg) rotateX(10deg) scale(0.95); 
          }
        }
        a.footerLink {
          color: #ffffff;
          text-decoration: none;
          border-bottom: 1px solid transparent;
          transition: border-bottom 0.3s ease;
        }
        a.footerLink:hover {
          border-bottom: 1px solid #ffffff;
        }
      `}</style>

      <footer style={styles.footer}>
        {/* Sección superior con el logo y el texto animado */}
        <div style={styles.container}>
          <div style={styles.logoContainer}>
            <img 
              src={logoImage} 
              alt="Heritage Car Airport Inc. Logo" 
              style={styles.logoImage}
            />
            <div style={styles.textContainer}>
              <h1 style={styles.title}>HERITAGE</h1>
              <p style={styles.subtitle}>CAR AIRPORT INC.</p>
            </div>
          </div>
        </div>

        {/* Separador elegante con texto centrado */}
        <div style={styles.dividerContainer}>
          <hr style={styles.dividerLine} />
          <span style={styles.dividerText}>Local and long-distance destinations</span>
          <hr style={styles.dividerLine} />
        </div>

        {/* Sección inferior con 3 columnas: Service Areas, Premium Airport Transfers y Contact Us */}
        <div style={styles.infoContainer}>
          {/* Columna 1: Service Areas */}
          <div style={styles.column}>
            <h3 style={styles.columnTitle}>Service Areas</h3>
            <ul style={styles.list}>
              <li>Northern and Southern Westchester</li>
              <li>Dutchess County</li>
              <li>Orange County</li>
              <li>Putnam County</li>
              <li>Rockland County</li>
              <li>New York City</li>
            </ul>
          </div>

          {/* Columna 2: Premium Airport Transfers */}
          <div style={styles.column}>
            <h3 style={styles.columnTitle}>Premium Airport Transfers</h3>
            <p style={styles.columnText}>
              We provide reliable and punctual transfers to major airports, including:
            </p>
            <ul style={styles.list}>
              <li>John F. Kennedy International Airport (JFK)</li>
              <li>Newark Liberty International Airport (EWR)</li>
              <li>LaGuardia Airport (LGA)</li>
              <li>Westchester County Airport (HPN)</li>
              <li>Stewart International Airport</li>
            </ul>
          </div>

          {/* Columna 3: Contact Us */}
          <div style={styles.column}>
            <h3 style={styles.columnTitle}>Contact Us</h3>
            <p style={styles.columnText}>
              Phone: 
              <a className="footerLink" href="tel:+19142762961"> (914) 276-2961</a> | 
              <a className="footerLink" href="tel:+18452258161"> (845) 225-8161</a>
            </p>
            <p style={styles.columnText}>
              Email: 
              <a className="footerLink" href="mailto:heritagecarairport@gmail.com"> heritagecarairport@gmail.com</a>
            </p>
            <p style={styles.columnText}>
              Available 24/7 for all your transportation needs.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

const styles = {
  footer: {
    background: 'linear-gradient(to right, #0f2027, #203a43, #2c5364)', // mismo degradado que la barra de navegación
    padding: '20px 40px',
    display: 'flex',
    flexDirection: 'column', // Ahora en columna para acomodar secciones
    justifyContent: 'flex-start',
    alignItems: 'center'
  },
  container: {
    width: '100%',
    maxWidth: '1200px',
    margin: 0,
    textAlign: 'left'
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  logoImage: {
    maxWidth: '120px',
    width: '100%',
    // Sombra múltiple para un contorno elegante y profundo
    boxShadow: `
      0 0 0 3px #ffffff, 
      0 0 0 6px #203a43, 
      0 0 0 9px #2c5364, 
      8px 16px 30px rgba(0, 0, 0, 0.7)
    `,
    transform: 'perspective(800px) rotateY(20deg) rotateX(10deg) scale(1)',
    transition: 'transform 0.3s ease',
    animation: 'fadeInPulseOut 10s infinite'
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  title: {
    margin: 0,
    fontSize: '32px',
    color: '#ffffff',
    fontFamily: "'Georgia', serif",
    textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
  },
  subtitle: {
    margin: 0,
    fontSize: '18px',
    color: '#ffffff',
    fontFamily: "'Arial', sans-serif",
    letterSpacing: '1px',
    textShadow: '1px 1px 3px rgba(0,0,0,0.8)'
  },
  // Estilos para el separador
  dividerContainer: {
    width: '100%',
    maxWidth: '1200px',
    marginTop: '20px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  dividerLine: {
    flex: 1,
    border: 'none',
    borderTop: '1px solid rgba(255, 255, 255, 0.5)',
    margin: '0 10px'
  },
  dividerText: {
    color: '#ffffff',
    fontStyle: 'italic',
    fontSize: '16px',
    whiteSpace: 'nowrap'
  },
  // Estilos para la sección inferior
  infoContainer: {
    width: '100%',
    maxWidth: '1200px',
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap' // Para que en pantallas pequeñas se ajuste
  },
  column: {
    flex: '1',
    minWidth: '250px',
    margin: '0 10px',
    marginBottom: '20px'
  },
  columnTitle: {
    color: '#ffffff',
    fontSize: '18px',
    marginBottom: '10px',
    fontWeight: 'bold',
    textTransform: 'uppercase'
  },
  columnText: {
    color: '#ffffff',
    marginBottom: '10px',
    lineHeight: '1.5'
  },
  list: {
    margin: 0,
    paddingLeft: '1.5rem',
    color: '#ffffff',
    lineHeight: '1.5'
  }
};

export default Footer;
